import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import TournamentSelector from '../components/TournamentSelector';
import { exportNodeToPdf } from '../lib/pdfExport';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { TrendingUp, TrendingDown, IndianRupee, AlertTriangle, ChevronDown, ChevronRight, Edit, Plus, Save, X, ChevronsDownUp, ChevronsUpDown, Search, Download } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ───────────────────── Chart helpers ─────────────────────
// Indian short-form for axis ticks (₹1.2L, ₹3.4Cr)
const formatINRShort = (v) => {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e7) return `${sign}₹${(abs / 1e7).toFixed(abs >= 1e8 ? 0 : 2)}Cr`;
  if (abs >= 1e5) return `${sign}₹${(abs / 1e5).toFixed(abs >= 1e6 ? 0 : 2)}L`;
  if (abs >= 1e3) return `${sign}₹${(abs / 1e3).toFixed(abs >= 1e4 ? 0 : 1)}K`;
  return `${sign}₹${abs}`;
};

const formatINRFull = (v) =>
  `₹${Number(v || 0).toLocaleString('en-IN')}`;

// Pick a bar color based on utilization (3-tier color bands per product spec):
// <80% Green (healthy) · 80–100% Yellow/Amber (at risk) · >100% Red (exceeded).
const utilColor = (pct) => {
  const n = Number(pct) || 0;
  if (n > 100) return '#DC2626'; // exceeded — red
  if (n >= 80) return '#F59E0B'; // at-risk — amber
  return '#10B981';              // healthy — green
};

// iPhone-style soft progress pill. Used on KPI cards and the new Cash Flow /
// Open PO / Revenue widgets. Auto-colors by utilization unless overridden.
//   pct        — 0..120+ percentage (overflow caps to bar end with red color)
//   color      — explicit override (skips utilColor)
//   trackTone  — 'soft' (default, low-contrast) | 'dark'
const IPhoneBar = ({ pct = 0, color, trackTone = 'soft', height = 8 }) => {
  const p = Math.max(0, Math.min(Number(pct) || 0, 120));
  const width = Math.min(p, 100); // visually never overflow the pill
  const fill = color || utilColor(p);
  return (
    <div
      className={`w-full rounded-full overflow-hidden ${
        trackTone === 'dark' ? 'bg-slate-200' : 'bg-muted/50'
      }`}
      style={{ height }}
    >
      <div
        className="h-full rounded-full transition-[width] duration-700 ease-out"
        style={{ width: `${width}%`, backgroundColor: fill }}
      />
    </div>
  );
};

// Render component — horizontal "iPhone-style" comparison bars. Allocated is
// the soft track, Consumed is overlaid in the utilization color. Click a row
// to drill into the Budget Head detail page (requires onBarClick prop).
const BudgetBarChart = ({ data, dataKey, onBarClick }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center text-sm text-muted-foreground">
        No data to display
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="budget-bar-list">
      {data.map((row) => {
        const allocated = Number(row.allocated || 0);
        const consumed = Number(row.consumed || 0);
        const pct = allocated > 0 ? (consumed / allocated) * 100 : 0;
        const label = row[dataKey] || '—';
        const clickable = Boolean(onBarClick && row.id);
        const remaining = Math.max(0, allocated - consumed);
        return (
          <div
            key={`${label}-${row.id || ''}`}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? () => onBarClick(row) : undefined}
            onKeyDown={
              clickable
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onBarClick(row);
                    }
                  }
                : undefined
            }
            data-testid={`budget-bar-row-${label}`}
            className={`group rounded-lg border border-border/60 bg-white/40 px-3 py-2.5 ${
              clickable ? 'cursor-pointer hover:border-foreground/30 hover:shadow-sm' : ''
            } transition`}
          >
            <div className="flex items-baseline justify-between gap-3 mb-1.5">
              <span className="text-sm font-medium text-foreground truncate" title={label}>
                {label}
              </span>
              <span
                className="text-[11px] font-semibold px-1.5 py-0.5 rounded tabular-nums shrink-0"
                style={{
                  backgroundColor: `${utilColor(pct)}1A`,
                  color: utilColor(pct),
                }}
              >
                {pct.toFixed(0)}%
              </span>
            </div>
            <IPhoneBar pct={pct} height={10} />
            <div className="mt-1.5 grid grid-cols-3 gap-2 text-[11px] text-muted-foreground">
              <span>
                Budget&nbsp;
                <span className="text-foreground tabular-nums">{formatINRShort(allocated)}</span>
              </span>
              <span className="text-center">
                Consumed&nbsp;
                <span className="text-foreground tabular-nums">{formatINRShort(consumed)}</span>
              </span>
              <span className="text-right">
                Remaining&nbsp;
                <span className="text-foreground tabular-nums">{formatINRShort(remaining)}</span>
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};
// ────────────────────────────────────────────────────────

const Dashboard = () => {
  const navigate = useNavigate();
  const { hasTabAccess, user } = useAuth();
  const isSuperAdmin = user?.is_super_admin === true;
  const [overview, setOverview] = useState(null);
  const [revenueOverview, setRevenueOverview] = useState(null);
  const [widgets, setWidgets] = useState(null);  // Batch-5 widgets: revenue_categories, open_po_tracker, cash_flow
  const [departmentBreakdown, setDepartmentBreakdown] = useState([]);
  const [breakdown, setBreakdown] = useState([]);
  const [groupedData, setGroupedData] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(''); // Start empty, let TournamentSelector set default
  const [expandedDepts, setExpandedDepts] = useState({});
  const [loading, setLoading] = useState(false);
  
  // Quick edit states
  const [editingBudgetId, setEditingBudgetId] = useState(null);
  const [budgetSearch, setBudgetSearch] = useState('');
  const dashboardRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const handleExportDashboard = async () => {
    if (!dashboardRef.current) return;
    setExporting(true);
    try {
      // Auto-expand every department before snapshot so PDF shows all data.
      setExpandedDepts(Object.fromEntries(groupedData.map((d) => [d.department, true])));
      // Wait one tick for React + DOM to flush.
      await new Promise((r) => setTimeout(r, 350));
      const fyLabel = selectedFiscalYear && selectedFiscalYear !== 'all'
        ? selectedFiscalYear
        : 'All Tournaments';
      await exportNodeToPdf(dashboardRef.current, {
        filename: `MPL_Dashboard_${fyLabel.replace(/[^A-Za-z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}`,
        title: 'MPL Finance — Dashboard Overview',
        subtitle: `${fyLabel} · all departments expanded`,
        orientation: 'portrait',
      });
      toast.success('Dashboard PDF exported');
    } catch (e) {
      toast.error(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };
  const [editAmount, setEditAmount] = useState('');
  const [addBudgetDialog, setAddBudgetDialog] = useState(false);
  const [newBudget, setNewBudget] = useState({
    budget_head: '',
    allocated_amount: '',
    department: '',
    fiscal_year: '',
  });

  // MPL Excel import — super-admin only. Two-step UX: pick file → see dry-run
  // diff in a modal → click Apply (or Cancel). All math + PO-impact comes from
  // the backend so the UI never has to re-implement the reconciliation rules.
  const [mplImportOpen, setMplImportOpen] = useState(false);
  const [mplImportFile, setMplImportFile] = useState(null);
  const [mplImportPreview, setMplImportPreview] = useState(null);
  const [mplImportLoading, setMplImportLoading] = useState(false);
  const [mplApplyDeletes, setMplApplyDeletes] = useState(true);
  const [mplApplying, setMplApplying] = useState(false);

  // Inline editor for a single budget head's per-leg split.
  const [legEditorOpen, setLegEditorOpen] = useState(false);
  const [legEditorItem, setLegEditorItem] = useState(null);
  const [legEditorValues, setLegEditorValues] = useState({ alloc_mens: 0, alloc_womens: 0, alloc_ground2: 0 });
  const [legEditorSaving, setLegEditorSaving] = useState(false);

  const openLegEditor = (item) => {
    setLegEditorItem(item);
    setLegEditorValues({
      alloc_mens: Number(item.alloc_mens || 0),
      alloc_womens: Number(item.alloc_womens || 0),
      alloc_ground2: Number(item.alloc_ground2 || 0),
    });
    setLegEditorOpen(true);
  };

  const saveLegEditor = async () => {
    if (!legEditorItem) return;
    setLegEditorSaving(true);
    try {
      const res = await axios.put(
        `${API}/budgets/${legEditorItem.id}/leg-split`,
        legEditorValues,
        { withCredentials: true }
      );
      toast.success(`Updated. New allocated total: ₹${Number(res.data.allocated_amount).toLocaleString('en-IN')}`);
      setLegEditorOpen(false);
      setLegEditorItem(null);
      fetchDashboardData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update legs');
    } finally {
      setLegEditorSaving(false);
    }
  };

  const resetMplImport = () => {
    setMplImportFile(null);
    setMplImportPreview(null);
    setMplApplyDeletes(true);
  };

  const fyForImport = selectedFiscalYear && selectedFiscalYear !== 'all' ? selectedFiscalYear : '';

  const runMplDryRun = async () => {
    if (!mplImportFile) {
      toast.error('Choose the MPL Excel file first');
      return;
    }
    if (!fyForImport) {
      toast.error('Pick a specific tournament (not "All") before importing');
      return;
    }
    setMplImportLoading(true);
    setMplImportPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', mplImportFile);
      const res = await axios.post(
        `${API}/budgets/import-mpl-excel?fiscal_year=${encodeURIComponent(fyForImport)}&dry_run=true`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setMplImportPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not parse Excel');
    } finally {
      setMplImportLoading(false);
    }
  };

  const applyMplImport = async () => {
    if (!mplImportFile || !fyForImport) return;
    if (!window.confirm(
      `Apply to ${fyForImport}?\n\n` +
      `· ${mplImportPreview.summary.amount_changes} amount/split changes\n` +
      `· ${mplImportPreview.summary.adds} new heads\n` +
      `· ${mplApplyDeletes ? mplImportPreview.summary.deletes - mplImportPreview.summary.deletes_blocked_by_po : 0} deletes` +
      (mplImportPreview.summary.deletes_blocked_by_po > 0
        ? `\n\n${mplImportPreview.summary.deletes_blocked_by_po} delete(s) will be SKIPPED — they have linked POs.` : '') +
      `\n\nA full backup is saved before any write.`
    )) return;

    setMplApplying(true);
    try {
      const fd = new FormData();
      fd.append('file', mplImportFile);
      const res = await axios.post(
        `${API}/budgets/import-mpl-excel?fiscal_year=${encodeURIComponent(fyForImport)}&dry_run=false&apply_deletes=${mplApplyDeletes}`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(
        `Updated ${res.data.applied.updated} · added ${res.data.applied.added} · deleted ${res.data.applied.deleted}`
      );
      setMplImportOpen(false);
      resetMplImport();
      fetchDashboardData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed');
    } finally {
      setMplApplying(false);
    }
  };
  
  // Permission check
  const canManageBudgets = hasTabAccess('dashboard');

  useEffect(() => {
    // Fetch dashboard data when fiscal year changes or on mount
    // Use 'all' if selectedFiscalYear is empty
    if (!selectedFiscalYear) return;
    fetchDashboardData();
  }, [selectedFiscalYear]);

  useEffect(() => {
    if (!selectedFiscalYear) return;
    if (selectedDepartment) {
      fetchBreakdownByDepartment(selectedDepartment);
    }
  }, [selectedDepartment, selectedFiscalYear]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Use 'all' if selectedFiscalYear is empty or 'all'
      const fiscalYear = selectedFiscalYear || 'all';
      const fiscalYearParam = fiscalYear !== 'all' ? `?fiscal_year=${fiscalYear}` : '';
      
      const [overviewRes, deptBreakdownRes, breakdownRes, groupedRes, deptsRes, revenueRes, widgetsRes] = await Promise.all([
        axios.get(`${API}/analytics/overview${fiscalYearParam}`, { withCredentials: true }),
        axios.get(`${API}/analytics/department-breakdown${fiscalYearParam}`, { withCredentials: true }),
        axios.get(`${API}/analytics/budget-breakdown${fiscalYearParam}`, { withCredentials: true }),
        axios.get(`${API}/analytics/grouped-by-department${fiscalYearParam}`, { withCredentials: true }),
        axios.get(`${API}/analytics/departments`, { withCredentials: true }),
        axios.get(`${API}/revenue/analytics/overview`, {
          params: { tournament: fiscalYear },
          withCredentials: true,
        }).catch(() => ({ data: null })),
        axios.get(`${API}/dashboard/widgets`, {
          params: fiscalYear !== 'all' ? { tournament: fiscalYear } : {},
          withCredentials: true,
        }).catch(() => ({ data: null })),
      ]);
      setOverview(overviewRes.data);
      setDepartmentBreakdown(deptBreakdownRes.data);
      setBreakdown(breakdownRes.data);
      setGroupedData(groupedRes.data);
      setDepartments(deptsRes.data);
      setRevenueOverview(revenueRes.data);
      setWidgets(widgetsRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchBreakdownByDepartment = async (dept) => {
    try {
      let url = `${API}/analytics/budget-breakdown`;
      const params = [];
      
      if (selectedFiscalYear !== 'all') {
        params.push(`fiscal_year=${selectedFiscalYear}`);
      }
      
      if (dept !== 'all') {
        params.push(`department=${encodeURIComponent(dept)}`);
      }
      
      if (params.length > 0) {
        url += `?${params.join('&')}`;
      }
      
      const response = await axios.get(url, { withCredentials: true });
      setBreakdown(response.data);
    } catch (error) {
      toast.error('Failed to load budget breakdown');
    }
  };

  const toggleDepartment = (deptName) => {
    setExpandedDepts(prev => ({
      ...prev,
      [deptName]: !prev[deptName]
    }));
  };


  // Quick edit handlers
  const handleEditClick = (budgetId, currentAmount) => {
    setEditingBudgetId(budgetId);
    setEditAmount(currentAmount.toString());
  };

  const handleCancelEdit = () => {
    setEditingBudgetId(null);
    setEditAmount('');
  };

  const handleSaveEdit = async (budgetId) => {
    try {
      const budget = breakdown.find(b => b.id === budgetId);
      await axios.put(`${API}/budgets/${budgetId}`, {
        budget_head: budget.budget_head,
        allocated_amount: parseFloat(editAmount),
        fiscal_year: budget.fiscal_year || selectedFiscalYear,
        department: budget.department,
      }, { withCredentials: true });
      
      toast.success('Budget amount updated successfully');
      setEditingBudgetId(null);
      setEditAmount('');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update budget amount');
    }
  };

  const handleAddBudget = async () => {    try {
      await axios.post(`${API}/budgets`, {
        budget_head: newBudget.budget_head,
        allocated_amount: parseFloat(newBudget.allocated_amount),
        fiscal_year: newBudget.fiscal_year,
        department: newBudget.department,
      }, { withCredentials: true });
      
      toast.success('Budget head added successfully');
      setAddBudgetDialog(false);
      setNewBudget({
        budget_head: '',
        allocated_amount: '',
        department: '',
        fiscal_year: selectedFiscalYear !== 'all' ? selectedFiscalYear : '',
      });
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add budget');
    }
  };

  const handleBudgetHeadClick = (budgetId) => {
    navigate(`/budget-head/${budgetId}`);
  };


  // Filter groupedData by search across departments AND budget heads (must be at top
  // level — hooks cannot be conditional, so this runs before the `if (loading)` early-exit).
  const filteredGroupedData = useMemo(() => {
    const q = budgetSearch.trim().toLowerCase();
    if (!q) return groupedData;
    return groupedData
      .map((d) => {
        const deptHit = (d.department || '').toLowerCase().includes(q);
        const matchedHeads = (d.budget_heads || []).filter((h) =>
          (h.budget_head || '').toLowerCase().includes(q)
        );
        if (deptHit) return d; // department matches → keep all heads
        if (matchedHeads.length === 0) return null;
        return { ...d, budget_heads: matchedHeads };
      })
      .filter(Boolean);
  }, [groupedData, budgetSearch]);

  // While searching, auto-expand any matching department so results are visible.
  useEffect(() => {
    if (!budgetSearch.trim()) return;
    setExpandedDepts((prev) => {
      const next = { ...prev };
      filteredGroupedData.forEach((d) => {
        next[d.department] = true;
      });
      return next;
    });
  }, [budgetSearch, filteredGroupedData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const COLORS = ['#064E3B', '#F97316', '#059669', '#D97706'];

  return (
    <div data-testid="dashboard" ref={dashboardRef} className="space-y-8 min-w-0">
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight">
            Dashboard Overview
          </h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">Track budget consumption and alerts</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportDashboard}
            disabled={exporting}
            data-testid="dashboard-export-pdf"
            data-html2canvas-ignore="true"
          >
            <Download className="h-4 w-4 mr-1.5" />
            {exporting ? 'Preparing…' : 'Export PDF'}
          </Button>
          <div className="w-full sm:w-48">
            <TournamentSelector
              value={selectedFiscalYear}
              onChange={setSelectedFiscalYear}
              showAllOption={true}
            />
          </div>
        </div>
      </div>

      {/* Fiscal Year Indicator — compact one-liner (Feb 2026 simplification:
          dropped the duplicated tournament name + 'fiscal year' boilerplate). */}
      {selectedFiscalYear !== 'all' && (
        <div className="bg-primary/5 border border-primary/20 rounded-md px-3 py-1.5 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Showing <span className="font-medium text-foreground">{selectedFiscalYear}</span>
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedFiscalYear('all')}
            className="h-7 px-2 text-primary hover:text-primary"
          >
            Clear filter
          </Button>
        </div>
      )}

      {/* Revenue KPIs */}
      {hasTabAccess('revenue') && revenueOverview && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-xl font-heading font-semibold tracking-tight">
                Revenue Overview
              </h2>
              <p className="text-xs text-muted-foreground">
                Sales orders, invoices and collections
                {selectedFiscalYear && selectedFiscalYear !== 'all' ? ` for ${selectedFiscalYear}` : ''}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/revenue')}
              data-testid="view-revenue-button"
            >
              View Revenue
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4 lg:gap-6">
            <Card
              data-testid="revenue-contract-card"
              className="border-border hover:shadow-md transition-all duration-200 cursor-pointer"
              onClick={() => navigate('/revenue')}
            >
              <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2 border-b border-border/50">
                <CardTitle className="text-xs sm:text-sm font-medium leading-tight min-w-0">Contract Value</CardTitle>
                <IndianRupee className="h-4 w-4 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
              </CardHeader>
              <CardContent className="pt-4">
                <div className="text-xl sm:text-2xl font-heading font-bold break-words">
                  ₹{(revenueOverview?.total_contract_value || 0).toLocaleString('en-IN')}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {revenueOverview?.sales_order_count || 0}{' '}
                  sales order{(revenueOverview?.sales_order_count || 0) === 1 ? '' : 's'}
                </p>
              </CardContent>
            </Card>

            <Card
              data-testid="revenue-invoiced-card"
              className="border-border hover:shadow-md transition-all duration-200 cursor-pointer"
              onClick={() => navigate('/revenue')}
            >
              <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2 border-b border-border/50">
                <CardTitle className="text-xs sm:text-sm font-medium leading-tight min-w-0">Invoiced</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
              </CardHeader>
              <CardContent className="pt-4">
                <div className="text-xl sm:text-2xl font-heading font-bold break-words">
                  ₹{(revenueOverview?.total_invoiced || 0).toLocaleString('en-IN')}
                </div>
              </CardContent>
            </Card>

            <Card
              data-testid="revenue-received-card"
              className="border-border hover:shadow-md transition-all duration-200 cursor-pointer"
              onClick={() => navigate('/revenue')}
            >
              <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2 border-b border-border/50">
                <CardTitle className="text-xs sm:text-sm font-medium leading-tight min-w-0">Received</CardTitle>
                <TrendingUp className="h-4 w-4 text-success flex-shrink-0" strokeWidth={1.5} />
              </CardHeader>
              <CardContent className="pt-4">
                <div className="text-xl sm:text-2xl font-heading font-bold text-success break-words">
                  ₹{(revenueOverview?.total_received || 0).toLocaleString('en-IN')}
                </div>
              </CardContent>
            </Card>

            <Card
              data-testid="revenue-pending-invoicing-card"
              className="border-border hover:shadow-md transition-all duration-200 cursor-pointer"
              onClick={() => navigate('/revenue')}
            >
              <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2 border-b border-border/50">
                <CardTitle className="text-xs sm:text-sm font-medium leading-tight min-w-0">Pending Invoicing</CardTitle>
                <AlertTriangle className="h-4 w-4 text-warning flex-shrink-0" strokeWidth={1.5} />
              </CardHeader>
              <CardContent className="pt-4">
                <div className="text-xl sm:text-2xl font-heading font-bold text-warning break-words">
                  ₹{(revenueOverview?.pending_invoicing || 0).toLocaleString('en-IN')}
                </div>
              </CardContent>
            </Card>

            <Card
              data-testid="revenue-outstanding-card"
              className="border-border hover:shadow-md transition-all duration-200 cursor-pointer"
              onClick={() => navigate('/revenue')}
            >
              <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2 border-b border-border/50">
                <CardTitle className="text-xs sm:text-sm font-medium leading-tight min-w-0">Outstanding</CardTitle>
                <TrendingDown className="h-4 w-4 text-destructive flex-shrink-0" strokeWidth={1.5} />
              </CardHeader>
              <CardContent className="pt-4">
                <div className="text-xl sm:text-2xl font-heading font-bold text-destructive break-words">
                  ₹{(revenueOverview?.pending_collection || 0).toLocaleString('en-IN')}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Revenue Categories card removed from Dashboard (Feb 2026 simplification).
          Same data is available on the /revenue page via the SO list — having
          it here duplicated the Revenue Overview KPIs and showed mostly empty
          rows for categories without SOs yet. */}

      {/* Expense / Budget Metrics */}
      <div>
        <div className="mb-3">
          <h2 className="text-xl font-heading font-semibold tracking-tight">Expense Overview</h2>
          <p className="text-xs text-muted-foreground">
            Budget allocation, consumption and remaining capacity
            {selectedFiscalYear && selectedFiscalYear !== 'all' ? ` for ${selectedFiscalYear}` : ''}
          </p>
        </div>
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 lg:gap-6 ${
            (overview?.at_risk_count || 0) + (overview?.exceeded_count || 0) > 0
              ? 'lg:grid-cols-4'
              : 'lg:grid-cols-3'
          }`}
        >
          <Card data-testid="total-allocated-card" className="border-border hover:shadow-md transition-all duration-200">
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2 border-b border-border/50">
              <CardTitle className="text-xs sm:text-sm font-medium leading-tight min-w-0">Total Budgeted</CardTitle>
              <IndianRupee className="h-4 w-4 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              <div className="text-2xl sm:text-3xl font-heading font-bold break-words">
                ₹{(overview?.total_allocated || 0).toLocaleString('en-IN')}
              </div>
              <IPhoneBar pct={overview?.consumption_percentage || 0} />
              <p className="text-[11px] text-muted-foreground">
                {(overview?.consumption_percentage || 0).toFixed(1)}% consumed
              </p>
            </CardContent>
          </Card>

          <Card data-testid="total-consumed-card" className="border-border hover:shadow-md transition-all duration-200">
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2 border-b border-border/50">
              <CardTitle className="text-xs sm:text-sm font-medium leading-tight min-w-0">Total Consumed</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              <div className="text-2xl sm:text-3xl font-heading font-bold text-accent break-words">
                ₹{(overview?.total_consumed || 0).toLocaleString('en-IN')}
              </div>
              <IPhoneBar pct={overview?.consumption_percentage || 0} />
              <p className="text-[11px] text-muted-foreground">
                {(overview?.consumption_percentage || 0).toFixed(1)}% of total budget
              </p>
            </CardContent>
          </Card>

          <Card data-testid="remaining-budget-card" className="border-border hover:shadow-md transition-all duration-200">
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2 border-b border-border/50">
              <CardTitle className="text-xs sm:text-sm font-medium leading-tight min-w-0">Remaining</CardTitle>
              <TrendingDown className="h-4 w-4 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              <div className="text-2xl sm:text-3xl font-heading font-bold text-success break-words">
                ₹{(overview?.total_remaining || 0).toLocaleString('en-IN')}
              </div>
              <IPhoneBar
                pct={
                  overview?.total_allocated
                    ? ((overview.total_remaining || 0) / overview.total_allocated) * 100
                    : 0
                }
                color="#10B981"
              />
              <p className="text-[11px] text-muted-foreground">
                {overview?.total_allocated
                  ? (((overview.total_remaining || 0) / overview.total_allocated) * 100).toFixed(1)
                  : '0.0'}
                % of total budget
              </p>
            </CardContent>
          </Card>

          {/* At Risk / Exceeded tile — only render when there's something to
              warn about. Avoids showing a permanent '0 / 0' on healthy data. */}
          {((overview?.at_risk_count || 0) + (overview?.exceeded_count || 0) > 0) && (
            <Card data-testid="at-risk-card" className="border-border hover:shadow-md transition-all duration-200">
              <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2 border-b border-border/50">
                <CardTitle className="text-xs sm:text-sm font-medium leading-tight min-w-0">At Risk / Exceeded</CardTitle>
                <AlertTriangle className="h-4 w-4 text-warning flex-shrink-0" strokeWidth={1.5} />
              </CardHeader>
              <CardContent className="pt-4 space-y-3">
                <div className="text-2xl sm:text-3xl font-heading font-bold">
                  {overview?.at_risk_count || 0} / {overview?.exceeded_count || 0}
                </div>
                <IPhoneBar
                  pct={
                    overview?.budget_heads_count
                      ? (((overview.at_risk_count || 0) + (overview.exceeded_count || 0)) /
                          overview.budget_heads_count) *
                        100
                      : 0
                  }
                  color={(overview?.exceeded_count || 0) > 0 ? '#DC2626' : '#F59E0B'}
                />
                <p className="text-[11px] text-muted-foreground">
                  Out of {overview?.budget_heads_count || 0} budget heads
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Liquidity Position */}
      {/* Liquidity widget removed from Dashboard (Feb 2026 simplification).
          It mirrored the Cash Flow → Actual tile and has its own deeper UI on
          the Bank page already. */}

      {/* Open PO Tracker — stacked horizontal bar by operational status */}
      {widgets?.open_po_tracker && (
        <Card data-testid="open-po-tracker-card" className="border-border">
          <CardHeader className="border-b border-border/50">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className="font-heading">Open PO Tracker</CardTitle>
                <CardDescription>
                  Total committed across {widgets.open_po_tracker.total_count} POs ·{' '}
                  <span className="text-foreground font-medium">
                    ₹{(widgets.open_po_tracker.total_value || 0).toLocaleString('en-IN')}
                  </span>
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/purchase-orders')}
                data-testid="open-po-view-button"
              >
                View POs
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            {(() => {
              const total = widgets.open_po_tracker.total_value || 0;
              const palette = {
                'Draft': '#94A3B8',
                'Pending Invoicing': '#F59E0B',
                'Partially Invoiced': '#0EA5E9',
                'Fully Invoiced': '#10B981',
              };
              return (
                <>
                  {total > 0 ? (
                    <div className="w-full overflow-hidden rounded-full bg-muted/50" style={{ height: 14 }} data-testid="open-po-stacked-bar">
                      <div className="flex h-full w-full">
                        {widgets.open_po_tracker.buckets.map((b) => {
                          const pct = total > 0 ? (b.value / total) * 100 : 0;
                          if (pct <= 0) return null;
                          return (
                            <div
                              key={b.label}
                              title={`${b.label}: ₹${(b.value || 0).toLocaleString('en-IN')} (${b.count})`}
                              className="h-full"
                              style={{
                                width: `${pct}%`,
                                backgroundColor: palette[b.label] || '#64748B',
                              }}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground italic">
                      No purchase orders for this scope yet.
                    </div>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                    {widgets.open_po_tracker.buckets.map((b) => {
                      const pct = total > 0 ? (b.value / total) * 100 : 0;
                      const color = palette[b.label] || '#64748B';
                      return (
                        <div
                          key={b.label}
                          data-testid={`open-po-bucket-${b.label}`}
                          className="rounded-lg border border-border/60 bg-white/40 p-3 space-y-1"
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className="inline-block h-2.5 w-2.5 rounded-sm flex-shrink-0"
                              style={{ backgroundColor: color }}
                            />
                            <span className="text-xs font-medium truncate" title={b.label}>
                              {b.label}
                            </span>
                          </div>
                          <div className="text-base font-semibold tabular-nums">
                            ₹{(b.value || 0).toLocaleString('en-IN')}
                          </div>
                          <div className="text-[11px] text-muted-foreground tabular-nums">
                            {b.count} PO{b.count === 1 ? '' : 's'} · {pct.toFixed(1)}%
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Cash Flow Reconciliation — FY-to-date opening + received - spent
          compared against the latest bank balance, with the most recent
          investigation note (if any) for transparency. */}
      {widgets?.cash_flow && (
        <Card data-testid="cash-flow-reconciliation-card" className="border-border">
          <CardHeader className="border-b border-border/50">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className="font-heading">Cash Flow Reconciliation</CardTitle>
                <CardDescription>
                  FY {widgets.cash_flow.opening_fy_used || widgets.cash_flow.fy_code} · opening → received → spent → computed vs actual bank balance
                  {widgets.cash_flow.opening_fy_used
                    && widgets.cash_flow.opening_fy_used !== widgets.cash_flow.fy_code
                    && <> · <span className="text-amber-700">opening rolled from FY {widgets.cash_flow.opening_fy_used}</span></>}
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/bank')}
                data-testid="cash-flow-view-bank-button"
              >
                Investigate in Bank
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Opening (FY)', value: widgets.cash_flow.opening_total, tone: 'neutral' },
                { label: 'Received', value: widgets.cash_flow.received, tone: 'positive' },
                { label: 'Spent', value: widgets.cash_flow.spent, tone: 'negative' },
                // "Computed" tile removed (Feb 2026): it was just Opening +
                // Received − Spent, derivable from the other three. The
                // variance banner below already surfaces the gap.
                { label: 'Actual (Bank)', value: widgets.cash_flow.actual, tone: 'neutral' },
              ].map((tile) => {
                const toneClass =
                  tile.tone === 'positive'
                    ? 'text-emerald-700'
                    : tile.tone === 'negative'
                    ? 'text-red-600'
                    : 'text-foreground';
                return (
                  <div
                    key={tile.label}
                    data-testid={`cash-flow-tile-${tile.label}`}
                    className="rounded-lg border border-border/60 bg-white/40 p-3"
                  >
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      {tile.label}
                    </div>
                    <div className={`mt-1 text-lg font-semibold tabular-nums ${toneClass}`}>
                      ₹{Number(tile.value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </div>
                  </div>
                );
              })}
            </div>
            {(() => {
              const variance = Number(widgets.cash_flow.variance || 0);
              const abs = Math.abs(variance);
              const tone =
                abs < 1 ? 'success' : abs < 1000 ? 'warning' : 'danger';
              const bg =
                tone === 'success'
                  ? 'bg-emerald-50 border-emerald-200'
                  : tone === 'warning'
                  ? 'bg-amber-50 border-amber-200'
                  : 'bg-red-50 border-red-200';
              const fg =
                tone === 'success'
                  ? 'text-emerald-700'
                  : tone === 'warning'
                  ? 'text-amber-700'
                  : 'text-red-700';
              return (
                <div
                  data-testid="cash-flow-variance"
                  className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border p-3 ${bg}`}
                >
                  <div>
                    <div className={`text-xs uppercase tracking-wide font-semibold ${fg}`}>
                      Variance (Computed − Actual)
                    </div>
                    <div className={`text-xl font-bold tabular-nums ${fg}`}>
                      ₹{variance.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </div>
                  </div>
                  <div className={`text-xs ${fg} sm:text-right`}>
                    {tone === 'success'
                      ? 'Books reconcile cleanly.'
                      : 'Investigate via the Bank tab — Variance Investigation Log captures the audit trail.'}
                  </div>
                </div>
              );
            })()}
            {widgets.cash_flow.latest_note && (
              <div
                data-testid="cash-flow-latest-note"
                className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 text-xs space-y-1"
              >
                <div className="flex items-center justify-between gap-2 text-amber-700">
                  <span className="font-semibold uppercase tracking-wide">
                    Latest investigation note
                  </span>
                  <span className="text-[10px] tabular-nums">
                    {(widgets.cash_flow.latest_note.created_at || '').slice(0, 19).replace('T', ' ')}
                  </span>
                </div>
                <div className="text-foreground whitespace-pre-line">
                  {widgets.cash_flow.latest_note.reason}
                </div>
                <div className="text-muted-foreground">
                  — {widgets.cash_flow.latest_note.created_by || 'unknown'}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}


      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-1 gap-8">
        {/* "Budget Consumption by Department" chart removed (Feb 2026
            simplification): the "Budget Head Details by Department" table
            further down already groups the same data by department with
            more context (per-leg columns + drill-in). */}

        {/* Budget Head Chart with Department Filter */}
        <Card data-testid="budget-chart" className="border-border">
          <CardHeader className="border-b border-border/50">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading">Budget Consumption by Head</CardTitle>
                <CardDescription>Allocated vs Consumed amounts</CardDescription>
              </div>
              <div className="w-64">
                <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                  <SelectTrigger data-testid="department-filter">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map((dept) => (
                      <SelectItem key={dept} value={dept}>
                        {dept}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <BudgetBarChart
              data={breakdown.slice(0, 10)}
              dataKey="budget_head"
              onBarClick={(row) => row?.id && handleBudgetHeadClick(row.id)}
            />
          </CardContent>
        </Card>

        {/* Grouped Budget Details Table */}
        <Card data-testid="budget-details-card" className="border-border">
          <CardHeader className="border-b border-border/50">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className="font-heading">Budget Head Details by Department</CardTitle>
                <CardDescription>Grouped view with expandable departments</CardDescription>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <Input
                    type="text"
                    value={budgetSearch}
                    onChange={(e) => setBudgetSearch(e.target.value)}
                    placeholder="Search department or budget head…"
                    className="h-9 w-72 pl-8 pr-8"
                    data-testid="budget-search-input"
                  />
                  {budgetSearch && (
                    <button
                      type="button"
                      aria-label="Clear search"
                      onClick={() => setBudgetSearch('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      data-testid="budget-search-clear"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setExpandedDepts(
                      Object.fromEntries(filteredGroupedData.map((d) => [d.department, true]))
                    )
                  }
                  data-testid="expand-all-departments"
                >
                  <ChevronsUpDown className="h-4 w-4 mr-1.5" />
                  Expand all
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setExpandedDepts({})}
                  data-testid="collapse-all-departments"
                >
                  <ChevronsDownUp className="h-4 w-4 mr-1.5" />
                  Collapse all
                </Button>
                {isSuperAdmin && (
                  <Button
                    size="sm"
                    variant="default"
                    onClick={() => setMplImportOpen(true)}
                    data-testid="import-mpl-excel-btn"
                    title="Import MPL Core Budget Excel (Mens / Womens / Ground 2 split)"
                  >
                    <Download className="h-4 w-4 mr-1.5 rotate-180" />
                    Import MPL Excel
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="overflow-x-auto">
              {filteredGroupedData.length === 0 && budgetSearch.trim() ? (
                <div className="text-center text-sm text-muted-foreground py-10" data-testid="budget-search-empty">
                  No departments or budget heads match &ldquo;<span className="font-semibold">{budgetSearch}</span>&rdquo;.
                </div>
              ) : null}
              {filteredGroupedData.map((deptData) => (
                <div key={deptData.department} className="mb-4">
                  {/* Department Header Row */}
                  <div
                    data-testid={`dept-row-${deptData.department}`}
                    className="flex items-center bg-primary/10 p-4 rounded-lg cursor-pointer hover:bg-primary/20 transition-colors border border-border"
                    onClick={() => toggleDepartment(deptData.department)}
                  >
                    <div className="flex items-center flex-1">
                      {expandedDepts[deptData.department] ? (
                        <ChevronDown className="h-5 w-5 mr-2" />
                      ) : (
                        <ChevronRight className="h-5 w-5 mr-2" />
                      )}
                      <span className="font-heading font-bold text-lg">
                        {deptData.department}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          setNewBudget({
                            ...newBudget,
                            department: deptData.department,
                            fiscal_year: selectedFiscalYear !== 'all' ? selectedFiscalYear : '',
                          });
                          setAddBudgetDialog(true);
                        }}
                        className="ml-4"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add
                      </Button>
                    </div>
                    <div className="grid grid-cols-4 gap-8 flex-1 text-right">
                      <div>
                        <div className="text-xs text-muted-foreground">Allocated</div>
                        <div className="font-semibold">
                          ₹{deptData.total_allocated.toLocaleString('en-IN')}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Consumed</div>
                        <div className="font-semibold text-accent">
                          ₹{deptData.total_consumed.toLocaleString('en-IN')}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Remaining</div>
                        <div className="font-semibold text-success">
                          ₹{deptData.total_remaining.toLocaleString('en-IN')}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Progress</div>
                        <div className="font-semibold">
                          {deptData.consumed_percentage.toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expandable Budget Heads */}
                  {expandedDepts[deptData.department] && (
                    <div className="mt-2 ml-8 border-l-2 border-primary/30 pl-4">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-muted/30 text-muted-foreground uppercase text-xs tracking-wider font-semibold">
                            <th className="text-left py-2 px-4">Budget Head</th>
                            <th className="text-right py-2 px-3 bg-blue-50/50 text-blue-700">Mens</th>
                            <th className="text-right py-2 px-3 bg-pink-50/50 text-pink-700">Womens</th>
                            <th className="text-right py-2 px-3 bg-amber-50/50 text-amber-700">Ground 2</th>
                            <th className="text-right py-2 px-4">Allocated</th>
                            <th className="text-right py-2 px-4">Consumed</th>
                            <th className="text-right py-2 px-4">Remaining</th>
                            <th className="text-right py-2 px-4">Progress</th>
                            <th className="text-center py-2 px-4">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {deptData.budget_heads.map((item) => (
                            <tr
                              key={item.id}
                              data-testid={`budget-head-${item.budget_head}`}
                              className="group border-b border-border/50 hover:bg-muted/20 transition-colors cursor-pointer"
                              onClick={() => handleBudgetHeadClick(item.id)}
                            >
                              <td className="py-2 px-4 text-sm font-medium hover:text-primary transition-colors">
                                <div>{item.budget_head}</div>
                                {/* Show "Common / Unallocated" pill only when there is no per-leg split. */}
                                {item.alloc_mens == null && item.alloc_womens == null && item.alloc_ground2 == null && (
                                  <div className="mt-1" data-testid={`leg-split-${item.id}`}>
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-600 text-[10px]">
                                      Common / Unallocated
                                    </span>
                                  </div>
                                )}
                              </td>
                              <td className="py-2 px-3 text-right font-mono text-sm bg-blue-50/30 text-blue-800">
                                {item.alloc_mens && item.alloc_mens > 0
                                  ? (<>
                                      <div>₹{Number(item.alloc_mens).toLocaleString('en-IN')}</div>
                                      {Number(item.consumed_mens || 0) > 0 && (
                                        <div className="text-[10px] font-normal text-blue-700/70">
                                          consumed ₹{Number(item.consumed_mens).toLocaleString('en-IN')}
                                        </div>
                                      )}
                                    </>)
                                  : <span className="text-muted-foreground">—</span>}
                              </td>
                              <td className="py-2 px-3 text-right font-mono text-sm bg-pink-50/30 text-pink-800">
                                {item.alloc_womens && item.alloc_womens > 0
                                  ? (<>
                                      <div>₹{Number(item.alloc_womens).toLocaleString('en-IN')}</div>
                                      {Number(item.consumed_womens || 0) > 0 && (
                                        <div className="text-[10px] font-normal text-pink-700/70">
                                          consumed ₹{Number(item.consumed_womens).toLocaleString('en-IN')}
                                        </div>
                                      )}
                                    </>)
                                  : <span className="text-muted-foreground">—</span>}
                              </td>
                              <td className="py-2 px-3 text-right font-mono text-sm bg-amber-50/30 text-amber-800">
                                {item.alloc_ground2 && item.alloc_ground2 > 0
                                  ? (<>
                                      <div>₹{Number(item.alloc_ground2).toLocaleString('en-IN')}</div>
                                      {Number(item.consumed_ground2 || 0) > 0 && (
                                        <div className="text-[10px] font-normal text-amber-700/70">
                                          consumed ₹{Number(item.consumed_ground2).toLocaleString('en-IN')}
                                        </div>
                                      )}
                                    </>)
                                  : <span className="text-muted-foreground">—</span>}
                              </td>
                              <td className="py-2 px-4 text-right text-sm" onClick={(e) => e.stopPropagation()}>
                                {editingBudgetId === item.id ? (
                                  <div className="flex items-center justify-end gap-2">
                                    <Input
                                      type="number"
                                      value={editAmount}
                                      onChange={(e) => setEditAmount(e.target.value)}
                                      className="w-32 h-7 text-sm"
                                      autoFocus
                                    />
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleSaveEdit(item.id)}
                                      className="h-7 px-2"
                                    >
                                      <Save className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={handleCancelEdit}
                                      className="h-7 px-2"
                                    >
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-end gap-2">
                                    <span>₹{item.allocated.toLocaleString('en-IN')}</span>
                                    {isSuperAdmin && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleEditClick(item.id, item.allocated)}
                                        className="h-6 px-1 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                                        title="Edit allocated amount (super admin only)"
                                        data-testid={`edit-budget-${item.id}`}
                                      >
                                        <Edit className="h-3 w-3" />
                                      </Button>
                                    )}
                                    {isSuperAdmin && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => openLegEditor(item)}
                                        className="h-6 px-1 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-[10px]"
                                        title="Edit per-leg split (Mens / Womens / Ground 2)"
                                        data-testid={`edit-leg-split-${item.id}`}
                                      >
                                        Legs
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </td>
                              <td className="py-2 px-4 text-right text-sm">
                                ₹{item.consumed.toLocaleString('en-IN')}
                              </td>
                              <td className="py-2 px-4 text-right text-sm">
                                ₹{item.remaining.toLocaleString('en-IN')}
                              </td>
                              <td className="py-2 px-4 text-right text-sm">
                                {item.consumed_percentage.toFixed(1)}%
                              </td>
                              <td className="py-2 px-4 text-center">
                                {item.status === 'exceeded' && (
                                  <Badge className="bg-destructive text-white text-xs">
                                    Exceeded
                                  </Badge>
                                )}
                                {item.status === 'warning' && (
                                  <Badge className="bg-warning text-white text-xs">
                                    Warning
                                  </Badge>
                                )}
                                {item.status === 'normal' && (
                                  <Badge className="bg-success text-white text-xs">
                                    Normal
                                  </Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                          {/* Department-level totals row — sums each leg
                              column so the user can compare Mens vs Womens
                              spend at a glance. */}
                          {(() => {
                            const sumLeg = (k) => deptData.budget_heads.reduce((s, h) => s + Number(h[k] || 0), 0);
                            const tM = sumLeg('alloc_mens');
                            const tW = sumLeg('alloc_womens');
                            const tG = sumLeg('alloc_ground2');
                            const tA = deptData.budget_heads.reduce((s, h) => s + Number(h.allocated || 0), 0);
                            return (
                              <tr className="border-t-2 border-primary/40 bg-muted/30 font-semibold text-sm" data-testid={`dept-totals-${deptData.department}`}>
                                <td className="py-2 px-4 uppercase tracking-wider text-xs text-muted-foreground">Department total</td>
                                <td className="py-2 px-3 text-right font-mono text-blue-800 bg-blue-50/40">
                                  {tM > 0 ? `₹${tM.toLocaleString('en-IN')}` : <span className="text-muted-foreground">—</span>}
                                </td>
                                <td className="py-2 px-3 text-right font-mono text-pink-800 bg-pink-50/40">
                                  {tW > 0 ? `₹${tW.toLocaleString('en-IN')}` : <span className="text-muted-foreground">—</span>}
                                </td>
                                <td className="py-2 px-3 text-right font-mono text-amber-800 bg-amber-50/40">
                                  {tG > 0 ? `₹${tG.toLocaleString('en-IN')}` : <span className="text-muted-foreground">—</span>}
                                </td>
                                <td className="py-2 px-4 text-right font-mono">₹{tA.toLocaleString('en-IN')}</td>
                                <td colSpan={4}></td>
                              </tr>
                            );
                          })()}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Alerts */}
      {overview?.recent_alerts && overview.recent_alerts.length > 0 && (
        <Card data-testid="recent-alerts-card" className="border-border">
          <CardHeader className="border-b border-border/50">
            <CardTitle className="font-heading">Recent Alerts</CardTitle>
            <CardDescription>Unread budget threshold notifications</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-3">
              {overview.recent_alerts.map((alert) => (
                <div
                  key={alert.id}
                  data-testid={`alert-${alert.id}`}
                  className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border border-border"
                >
                  <AlertTriangle
                    className={`h-5 w-5 ${
                      alert.alert_type === 'exceeded_100'
                        ? 'text-destructive'
                        : 'text-warning'
                    }`}
                    strokeWidth={1.5}
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{alert.message}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(alert.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}


      {/* Add Budget Dialog */}
      <Dialog open={addBudgetDialog} onOpenChange={setAddBudgetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Add New Budget Head</DialogTitle>
            <DialogDescription>
              Create a new budget head under {newBudget.department || 'selected department'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="budget_head">Budget Head Name *</Label>
              <Input
                id="budget_head"
                value={newBudget.budget_head}
                onChange={(e) => setNewBudget({...newBudget, budget_head: e.target.value})}
                placeholder="e.g., Marketing Campaigns"
              />
            </div>
            <div>
              <Label htmlFor="allocated_amount">Allocated Amount *</Label>
              <Input
                id="allocated_amount"
                type="number"
                value={newBudget.allocated_amount}
                onChange={(e) => setNewBudget({...newBudget, allocated_amount: e.target.value})}
                placeholder="e.g., 50000"
              />
            </div>
            <div>
              <Label htmlFor="department">Department</Label>
              <Input
                id="department"
                value={newBudget.department}
                readOnly
                className="bg-muted"
              />
            </div>
            <div>
              <Label htmlFor="fiscal_year">Tournament</Label>
              <TournamentSelector
                value={newBudget.fiscal_year}
                onChange={(value) => setNewBudget({...newBudget, fiscal_year: value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddBudgetDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddBudget}
              disabled={!newBudget.budget_head || !newBudget.allocated_amount || !newBudget.fiscal_year}
            >
              Add Budget Head
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MPL Excel import dialog (super-admin only). Shows the dry-run diff
          before any DB write; backend always backs up the pre-state. */}
      <Dialog
        open={mplImportOpen}
        onOpenChange={(v) => {
          setMplImportOpen(v);
          if (!v) resetMplImport();
        }}
      >
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="mpl-import-dialog">
          <DialogHeader>
            <DialogTitle>Import MPL Core Budget Excel</DialogTitle>
            <DialogDescription>
              Reconciles the uploaded Excel against{' '}
              <span className="font-semibold text-foreground">
                {fyForImport || '— pick a tournament first —'}
              </span>
              . Per-line splits (Mens / Womens additional / Ground 2) are read from columns D, E, F.
              A full backup of the current state is saved before any change is applied.
            </DialogDescription>
          </DialogHeader>

          {!mplImportPreview ? (
            <div className="space-y-3">
              <div>
                <Label>Excel file (.xlsx)</Label>
                <Input
                  type="file"
                  accept=".xlsx"
                  onChange={(e) => setMplImportFile(e.target.files?.[0] || null)}
                  data-testid="mpl-import-file-input"
                />
              </div>
              <div className="text-xs text-muted-foreground">
                Sheet must be named <span className="font-mono">Expenses</span> (or be the first sheet).
                Department-header rows are detected automatically; any row with a positive value in any of
                Mens / Womens / Ground 2 is imported.
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Summary tiles */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <DiffTile label="Amount changes" value={mplImportPreview.summary.amount_changes} />
                <DiffTile label="New heads" value={mplImportPreview.summary.adds} accent="text-emerald-600" />
                <DiffTile
                  label="To delete"
                  value={mplImportPreview.summary.deletes}
                  accent="text-destructive"
                />
                <DiffTile
                  label="Δ Total"
                  value={`₹${(mplImportPreview.summary.totals.excel - mplImportPreview.summary.totals.current).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
                  accent={
                    mplImportPreview.summary.totals.excel - mplImportPreview.summary.totals.current >= 0
                      ? 'text-emerald-700'
                      : 'text-destructive'
                  }
                />
              </div>

              <div className="text-xs text-muted-foreground">
                Current total: ₹{mplImportPreview.summary.totals.current.toLocaleString('en-IN')} →
                After import: ₹{mplImportPreview.summary.totals.excel.toLocaleString('en-IN')}
              </div>

              {mplImportPreview.summary.deletes_blocked_by_po > 0 && (
                <div className="text-xs p-2 rounded border border-amber-300 bg-amber-50 text-amber-800">
                  ⚠ {mplImportPreview.summary.deletes_blocked_by_po} of the deletes have linked POs and
                  will be skipped automatically.
                </div>
              )}

              <DiffSection
                title={`Amount / split changes (${mplImportPreview.diffs.length})`}
                rows={mplImportPreview.diffs.slice(0, 25).map((d) => ({
                  key: d.id,
                  cells: [
                    `[${d.department}] ${d.budget_head}`,
                    `₹${(d.old_amount).toLocaleString('en-IN')}`,
                    `→ ₹${(d.new_amount).toLocaleString('en-IN')}`,
                    `${d.delta >= 0 ? '+' : ''}₹${d.delta.toLocaleString('en-IN')}`,
                    `POs: ${d.po_count}`,
                  ],
                }))}
                more={mplImportPreview.diffs.length > 25 ? mplImportPreview.diffs.length - 25 : 0}
              />

              <DiffSection
                title={`New heads to add (${mplImportPreview.adds.length})`}
                rows={mplImportPreview.adds.map((a) => ({
                  key: `${a.department}|${a.budget_head}`,
                  cells: [
                    `[${a.department}] ${a.budget_head}`,
                    `₹${a.allocated_amount.toLocaleString('en-IN')}`,
                  ],
                }))}
                accent="text-emerald-700"
              />

              <DiffSection
                title={`Heads to delete (${mplImportPreview.deletes.length})`}
                rows={mplImportPreview.deletes.map((d) => ({
                  key: d.id,
                  cells: [
                    `[${d.department}] ${d.budget_head}`,
                    `₹${d.amount.toLocaleString('en-IN')}`,
                    d.po_count > 0
                      ? `🚨 ${d.po_count} PO(s) linked — will be SKIPPED`
                      : 'no POs',
                  ],
                }))}
                accent="text-destructive"
              />

              <label className="flex items-center gap-2 text-sm pt-1">
                <input
                  type="checkbox"
                  checked={mplApplyDeletes}
                  onChange={(e) => setMplApplyDeletes(e.target.checked)}
                  data-testid="mpl-import-apply-deletes"
                />
                Also apply deletes (POs prevent unsafe deletes automatically)
              </label>
            </div>
          )}

          <DialogFooter className="gap-2">
            {mplImportPreview ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setMplImportPreview(null)}
                  disabled={mplApplying}
                >
                  Pick different file
                </Button>
                <Button onClick={applyMplImport} disabled={mplApplying} data-testid="mpl-import-apply-btn">
                  {mplApplying ? 'Applying…' : 'Apply changes'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setMplImportOpen(false)} disabled={mplImportLoading}>
                  Cancel
                </Button>
                <Button
                  onClick={runMplDryRun}
                  disabled={mplImportLoading || !mplImportFile || !fyForImport}
                  data-testid="mpl-import-preview-btn"
                >
                  {mplImportLoading ? 'Reading…' : 'Preview diff'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inline editor for a single budget head's per-leg split */}
      <Dialog
        open={legEditorOpen}
        onOpenChange={(v) => {
          setLegEditorOpen(v);
          if (!v) setLegEditorItem(null);
        }}
      >
        <DialogContent className="max-w-md" data-testid="leg-editor-dialog">
          <DialogHeader>
            <DialogTitle>Edit leg split</DialogTitle>
            <DialogDescription>
              {legEditorItem ? (
                <>
                  <span className="font-semibold text-foreground">{legEditorItem.budget_head}</span>
                  <span className="text-muted-foreground"> · current allocated ₹{Number(legEditorItem.allocated || 0).toLocaleString('en-IN')}</span>
                </>
              ) : null}
              <div className="mt-1 text-xs">
                The total allocated amount will be set to the sum of the three legs.
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {[
              { key: 'alloc_mens', label: 'Mens (+ Holkar)', color: 'border-blue-300 bg-blue-50/40' },
              { key: 'alloc_womens', label: 'Womens (additional)', color: 'border-pink-300 bg-pink-50/40' },
              { key: 'alloc_ground2', label: 'Ground 2 (Daly)', color: 'border-amber-300 bg-amber-50/40' },
            ].map((leg) => (
              <div key={leg.key} className={`border rounded-md p-2 ${leg.color}`}>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">{leg.label}</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={legEditorValues[leg.key] === 0 ? '' : legEditorValues[leg.key]}
                  onChange={(e) => setLegEditorValues({
                    ...legEditorValues,
                    [leg.key]: e.target.value === '' ? 0 : Number(e.target.value),
                  })}
                  placeholder="0"
                  className="mt-1 font-mono"
                  data-testid={`leg-editor-${leg.key}`}
                />
              </div>
            ))}
            <div className="text-sm font-mono text-right text-muted-foreground">
              New allocated total: ₹{
                (Number(legEditorValues.alloc_mens || 0) +
                 Number(legEditorValues.alloc_womens || 0) +
                 Number(legEditorValues.alloc_ground2 || 0)).toLocaleString('en-IN')
              }
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLegEditorOpen(false)} disabled={legEditorSaving}>Cancel</Button>
            <Button onClick={saveLegEditor} disabled={legEditorSaving} data-testid="leg-editor-save">
              {legEditorSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

const DiffTile = ({ label, value, accent = '' }) => (
  <div className="border rounded-md p-2 bg-muted/20">
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className={`text-lg font-bold font-mono ${accent}`}>{value}</div>
  </div>
);

const DiffSection = ({ title, rows, more = 0, accent = '' }) => {
  if (!rows.length) return null;
  return (
    <div>
      <div className={`text-sm font-semibold mb-1 ${accent}`}>{title}</div>
      <div className="border rounded-md max-h-48 overflow-y-auto">
        <table className="w-full text-xs">
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-b last:border-b-0">
                {r.cells.map((c, i) => (
                  <td key={i} className="px-2 py-1 font-mono align-top">{c}</td>
                ))}
              </tr>
            ))}
            {more > 0 && (
              <tr>
                <td className="px-2 py-1 text-muted-foreground italic" colSpan={5}>
                  …and {more} more (full list applied on confirm)
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Dashboard;
